import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import {
  BoltOutlined,
  Co2Outlined,
  CompressOutlined,
  DirectionsCarOutlined,
  ElectricalServicesOutlined,
  EnergySavingsLeafOutlined,
  EngineeringOutlined,
  InventoryOutlined,
  LocalShippingOutlined,
  OpacityOutlined,
  PublicOutlined,
  RecyclingOutlined,
  SpeedOutlined,
  WorkOutlined,
} from "@mui/icons-material";

import ContentBackgroundedSection from "../components/ContentBackgroundedSection";
import ContentSection from "../components/ContentSection";
import LinkButton from "../components/ui/LinkButton";
import IconedItemsList from "../components/ui/IconedItemsList";

import GreensAndSkyLandscape from "../assets/images/GreensAndSkyLandscape.jpg";
import GreenMountainsLandscape from "../assets/images/GreenMountainsLandscape.jpg";
import InfographicBiogasToBiomethaneProcess from "../assets/images/InfographicBiogasToBiomethaneProcess.jpg";
import BiogasPlantSideView from "../assets/images/BiogasPlantSideView.jpg";
import BiogasPlantAerialView from "../assets/images/BiogasPlantAerialView.jpg";
import InfographicPurificationLiquefactionCompression from "../assets/images/InfographicPurificationLiquefactionCompression.jpg";
import BiogasCar from "../assets/images/BiogasCar.jpg";
import BiogasFacilities from "../assets/images/BiogasFacilities.jpg";
import NighttimeTruckRoad from "../assets/images/NighttimeTruckRoad.jpg";
import HeroSectionV2 from "../components/HeroSectionV2";

function BiogasPage() {
  return (
    <Fragment>
            <HeroSectionV2
        heading="Expanding into Green Energy Infrastructure"
        subheading="Investing in the Future of Sustainable Energy"
      >
        <Box display="flex" gap={6} flexDirection={{ xs: "column", lg: "row" }}>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography
              color="#001b35"
              variant="h6"
              sx={{ flex: 1, flexShrink: 0 }}
            >
              Flamestone is expanding its expertise into the biogas and
              biomethane sectors, investing in biogas plants, biomethane plants,
              BioLNG facilities, and BioCNG production sites.
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              While we have historically excelled in hardware design, software
              development, and technology solutions, we are now actively
              channeling our knowledge and resources into the growing field of
              sustainable energy.
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              For over a decade, Flamestone has developed and supported advanced
              hardware and software systems, partnering with global
              manufacturers such as Foxconn and Pegatron. These foundations have
              equipped us to transition into the energy sector, where we aim to
              support innovation and growth by enabling the development of
              efficient and scalable renewable energy solutions.
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundImage: `url(${GreensAndSkyLandscape})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 8,
              minHeight: "200px",
            }}
          />
        </Box>
      </HeroSectionV2>
      <ContentSection>
        <Box
          marginTop={{ xs: 0, lg: 6 }}
          sx={{
            borderTop: "5px solid #1B5E20",
            borderRadius: 6,
            paddingTop: 6,
            textAlign: "center",
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography
              color="#001b35"
              variant="h3"
              sx={{
                flex: 1,
                flexShrink: 0,
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Why we’re Investing in Biogas and Biomethane Plants
            </Typography>
            <Typography
              color="#001b35"
              variant="body1"
              margin="0 auto"
              marginTop={3}
              maxWidth={1000}
            >
              Biogas plants represent a critical step in sustainable energy
              production. By capturing and processing organic waste, these
              facilities generate biogas, a renewable resource that reduces
              landfill waste and lowers greenhouse gas emissions.
            </Typography>
          </Box>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <img
              alt="Infographic of the biogas-to-biomethane process"
              src={InfographicBiogasToBiomethaneProcess}
              style={{
                width: "600px",
                maxWidth: "100%",
                margin: "0 auto",
                marginTop: "48px",
              }}
            />
          </Box>
        </Box>
      </ContentSection>
      <ContentSection>
        <Box
          marginTop={{ xs: 0, lg: 6 }}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <img
              alt=""
              src={BiogasPlantSideView}
              style={{
                borderTop: "5px solid #1B5E20",
                borderRadius: "32px 32px 3px 3px",
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
            <Typography marginTop={2} color="#001b35" variant="h6">
              Our investments in biogas plants aims to:
            </Typography>
            <IconedItemsList
              marginBlockStart={2}
              items={[
                {
                  heading: "Support Sustainable Waste Management",
                  description: "Turning organic waste into usable energy.",
                  icon: RecyclingOutlined,
                },
                {
                  heading: "Create a Platform for Downstream Applications",
                  description:
                    "Feeding purified gas into biomethane, BioLNG, and BioCNG systems.",
                  icon: EngineeringOutlined,
                },
              ]}
            />
          </Box>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <img
              alt=""
              src={BiogasPlantAerialView}
              style={{
                borderTop: "5px solid #1B5E20",
                borderRadius: "32px 32px 3px 3px",
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
            <Typography marginTop={2} color="#001b35" variant="h6">
              Our role in biomethane plant development
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Biomethane plants purify biogas into a high-grade, clean energy
              source that can be injected into natural gas grids or used for
              other applications. Flamestone is committed to investing in
              projects that can enhance biomethane production, contributing to:
            </Typography>
            <IconedItemsList
              marginBlockStart={2}
              items={[
                {
                  heading: "Cleaner Energy for Communities",
                  description:
                    "Providing sustainable alternatives to fossil fuels.",
                  icon: EnergySavingsLeafOutlined,
                },
                {
                  heading: "Grid Integration",
                  description:
                    "Supporting the expansion of renewable energy infrastructure.",
                  icon: ElectricalServicesOutlined,
                },
              ]}
            />
          </Box>
        </Box>
      </ContentSection>
      <ContentSection>
        <Box
          marginTop={{ xs: 0, lg: 6 }}
          sx={{
            borderTop: "5px solid #1B5E20",
            borderRadius: 6,
            paddingTop: 6,
            textAlign: "center",
          }}
        >
          <Typography
            color="#001b35"
            variant="h3"
            sx={{ flex: 1, flexShrink: 0 }}
          >
            Why BioLNG and BioCNG matter
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            margin="0 auto"
            marginTop={3}
            maxWidth={1000}
          >
            In many biogas plants, gas grid injection of biomethane is
            infeasible due to multiple reasons, such the distance from the gas
            grid or landowner objections. Perfect solutions, in these cases are
            BioLNG (liquefied biomethane) and BioCNG (compressed biomethane) as
            they represent the future of sustainable transport fuels. As the
            world moves toward decarbonisation, these fuels provide an
            eco-friendly alternative to diesel and petrol.
          </Typography>
          <img
            alt="Infographic of purification, liquefaction, compression"
            src={InfographicPurificationLiquefactionCompression}
            style={{
              width: "700px",
              maxWidth: "100%",
              margin: "0 auto",
              marginTop: "48px",
            }}
          />
        </Box>
      </ContentSection>
      <ContentSection>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <img
              alt=""
              src={BiogasCar}
              style={{
                borderTop: "5px solid #1B5E20",
                borderRadius: "32px 32px 3px 3px",
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
            <Typography marginTop={2} color="#001b35" variant="h6">
              Focusing on BioCNG Development
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Compressed biomethane (BioCNG) offers a more accessible, renewable
              fuel for localised transportation and industrial uses.
              Flamestone’s investments in BioCNG aim to:
            </Typography>
            <IconedItemsList
              marginBlockStart={2}
              items={[
                {
                  heading: "Expand Green Mobility",
                  description: "Powering buses, cars, and light-duty vehicles.",
                  icon: DirectionsCarOutlined,
                },
                {
                  heading: "Support Local Economies",
                  description:
                    "Creating jobs and reducing reliance on imported fuels.",
                  icon: WorkOutlined,
                },
              ]}
            />
          </Box>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <img
              alt=""
              src={BiogasFacilities}
              style={{
                borderTop: "5px solid #1B5E20",
                borderRadius: "32px 32px 3px 3px",
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
            />
            <Typography marginTop={2} color="#001b35" variant="h6">
              Investing in BioLNG Facilities
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Flamestone is actively investing in BioLNG production, where
              biomethane is liquefied to create a dense, transportable fuel.
              BioLNG is ideal for:
            </Typography>
            <IconedItemsList
              marginBlockStart={2}
              items={[
                {
                  heading: "Long-Haul Transport",
                  description:
                    "Reducing emissions in trucking and heavy-duty logistics.",
                  icon: LocalShippingOutlined,
                },
                {
                  heading: "Global Supply Chains",
                  description:
                    "Enabling efficient storage and international transport.",
                  icon: PublicOutlined,
                },
              ]}
            />
          </Box>
        </Box>
      </ContentSection>
      <ContentSection
        sx={{
          padding: { xs: 0 },
          marginTop: {xs: 6, lg: 8},
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(to bottom, #E8F5E9, white)",
            borderRadius: 6,
            padding: { xs: 4, lg: 6 },
          }}
        >
          <Box
            display="flex"
            gap={6}
            flexDirection={{ xs: "column", lg: "row" }}
          >
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6" marginTop={2}>
                Volume and Conversion of Biogas into Liquefied Methane (LNG)
                Biogas, produced in bioreactors, typically contains 50–70%
                methane (CH₄) and 30–50% carbon dioxide (CO₂), with small
                impurities.
              </Typography>
              <Typography color="#001b35" variant="body1" marginTop={2}>
                Through purification, this biogas is transformed into usable
                methane:
              </Typography>
              <IconedItemsList
                marginBlockStart={2}
                items={[
                  {
                    heading: "Efficiency",
                    description:
                      "1 m³ of biogas yields approximately 0.5 m³ of purified methane.",
                    icon: SpeedOutlined,
                  },
                  {
                    heading: "Liquefaction Advantage",
                    description:
                      "Methane is liquefied, reducing its volume by 600 times.",
                    icon: CompressOutlined,
                  },
                  {
                    heading: "1.7 litres of LNG",
                    description:
                      "1 m³ of purified methane when liquefied becomes approximately 1.7 litres of LNG.",
                    icon: OpacityOutlined,
                  },
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <img
                alt=""
                src={NighttimeTruckRoad}
                style={{
                  borderRadius: 32,
                  width: "100%",
                  maxHeight: "500px",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            gap={6}
            marginBlockStart={6}
            flexDirection={{ xs: "column", lg: "row" }}
          >
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6" marginTop={2}>
                Environmental Benefits
              </Typography>
              <Typography color="#001b35" variant="body1" marginTop={2}>
                Using liquefied biomethane as a fuel has significant
                environmental advantages:
              </Typography>
              <IconedItemsList
                marginBlockStart={2}
                items={[
                  {
                    heading: "CO₂ Reduction",
                    description:
                      "Biomethane can reduce carbon emissions by 50–70%, depending on the conditions of use.",
                    icon: Co2Outlined,
                  },
                  {
                    heading: "Impact Per Truck",
                    description:
                      "Each truck running on biomethane reduces CO₂ emissions by approximately 20 tonnes annually.",
                    icon: LocalShippingOutlined,
                  },
                ]}
              />
            </Box>
            <Box sx={{ flex: 1, flexShrink: 0 }}>
              <Typography color="#001b35" variant="h6" marginTop={2}>
                Energy Efficiency
              </Typography>
              <Typography color="#001b35" variant="body1" marginTop={2}>
                Liquefied biomethane has a high energy density, making it an
                excellent alternative to traditional fuels:
              </Typography>
              <IconedItemsList
                marginBlockStart={2}
                items={[
                  {
                    heading: "Energy Equivalent",
                    description:
                      "1 kg of LNG provides the same energy as approximately 1.39 litres of diesel fuel.",
                    icon: BoltOutlined,
                  },
                  {
                    heading: "Compact Storage",
                    description:
                      "LNG’s liquefied state allows for efficient storage and transport.",
                    icon: InventoryOutlined,
                  },
                ]}
              />
            </Box>
          </Box>
        </Box>
      </ContentSection>
      <ContentBackgroundedSection
        backgroundImage={GreenMountainsLandscape}
        sx={{ marginTop: 8, marginBottom: 8 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "center",
            margin: "0 auto",
            gap: 6,
            maxWidth: "1170px",
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0, paddingRight: { xs: 0, lg: 3 } }}>
            <Typography color="white" variant="h2" marginBlockStart={2}>
              Our Vision for Sustainable Energy
            </Typography>
            <Typography
              color="white"
              variant="body1"
              fontWeight={500}
              marginBlockStart={1}
            >
              Let us collaborate to shape the next generation of sustainable
              energy solutions. Flamestone is ready to invest in partnerships
              that align with our vision for a greener tomorrow.
            </Typography>
          </Box>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography
              color="white"
              variant="body1"
              fontWeight={500}
              fontStyle={"italic"}
            >
              Flamestone’s history in hardware and software development is a
              cornerstone of our expansion into the energy sector. By combining
              our technical capabilities with sustainable energy goals, we’re
              uniquely positioned to invest, support and create opportunities to
              drive meaningful change in renewable energy production. By
              focusing on{" "}
              <b>biogas plants, biomethane purification, BioLNG, and BioCNG</b>,
              we aim to foster innovation, reduce emissions, and support a
              transition to a cleaner energy future.
            </Typography>
            <Box marginTop={2}>
              <LinkButton color="tertiary" to="/services">
                Contact us
              </LinkButton>
            </Box>
          </Box>
        </Box>
      </ContentBackgroundedSection>
    </Fragment>
  );
}

export default BiogasPage;
